import { Badge } from "src/components/ui/Badge";
import BackButton from "../../components/ui/BackButton";
import { Product } from "../../types/products";
import EditProductModal from "../../components/ui/EditProductModal";
import { Plus } from "lucide-react";
import AddStockModal from "../../components/ui/AddStockModal";
import { toast } from "react-toastify";
import { IMAGE_CDN } from "src/redux/api/appConst";
import { deleteProduct } from "../../api/product.api";
import DeleteProductDialog from "../../components/ui/DeleteProductDialog";
import { useInventory } from "../../hooks/inventory/useInventory";
import { useEffect, useState } from "react";

function ProductDetailsView({
  activeProduct,
  onBack,
  autoOpenStock,
  clearAutoOpen,
}: {
  activeProduct: Product;
  onBack: () => void;
  autoOpenStock: boolean;
  clearAutoOpen: () => void;
}) {
  const [stockOpen, setStockOpen] = useState(false);

  const { data: quantity = 0 } = useInventory(activeProduct.id);

  const handleRemoveProduct = async (name: string, id: string) => {
    try {
      const res = await deleteProduct(id);
      if (res?.message?.toLowerCase() === "success") {
        toast.success(`Product ${name} Removed.`);
        onBack();
      } else {
        onBack();
      }
    } catch (error) {
      toast.error("failed to Delete Product");
      console.error(error);
    }
  };

  useEffect(() => {
    if (autoOpenStock) {
      setStockOpen(true);
      clearAutoOpen();
    }
  }, [autoOpenStock, clearAutoOpen]);

  console.log(activeProduct, 'product ');

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-6">
        <BackButton onClick={onBack} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Product Visuals */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 flex items-center justify-center aspect-square relative group">
            <img
              src={`${IMAGE_CDN}/${activeProduct.thumbnail}`}
              alt={activeProduct.name}
              className="w-full h-full object-contain"
            />
            <Badge
              className={`absolute top-6 right-6 px-2 py-1 rounded-full text-xs font-black tracking-widest shadow-xl text-white ${quantity === 0 ? "bg-rose-500" : quantity < 20 ? "bg-red-500" : "bg-emerald-500"}`}
            >
              {quantity === 0 ? "STOCK OUT" : `${quantity} UNITS AVAILABLE`}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-2xl border border-slate-100">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Pricing
              </p>
              <p className="text-xl font-black text-slate-900 mt-1">
                ₹{activeProduct.price}
              </p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-slate-100">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Inventory
              </p>
              {
                <p
                  className={`text-xl font-black mt-1 ${quantity === 0 ? "text-red-500" : quantity < 20 ? "text-rose-500" : "text-emerald-600"}`}
                >
                  {quantity && quantity} units
                </p>
              }
            </div>
          </div>
        </div>

        {/* Product Info */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-slate-100 h-full flex flex-col">
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="text-xs font-black text-emerald-600 uppercase tracking-[0.2em] mb-2">
                  {activeProduct.category}
                </p>
                <h1 className="text-4xl font-black text-slate-900 tracking-tight">
                  {activeProduct.name}
                </h1>
                <p className="text-xs font-black text-emerald-600 uppercase tracking-[0.2em] mb-2">
                  {activeProduct.sub_category}
                </p>
              </div>
              <EditProductModal product={activeProduct}>
                <button className="bg-slate-900 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10 active:scale-95 flex items-center gap-2">
                  <span>✏️</span> Edit Details
                </button>
              </EditProductModal>
            </div>

            <div className="flex-1 space-y-8">
              <div className="space-y-4">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">
                  Description
                </h4>
                <p className="text-slate-600 leading-relaxed text-lg font-medium">
                  {activeProduct.description ||
                    "No description provided for this supplement."}
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">
                  Benefits
                </h4>
                <p className="text-slate-600 leading-relaxed text-lg font-medium">
                  <ul>
                    {activeProduct.benefits.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                  {!activeProduct.benefits &&
                    "No Benefits provided for this supplement."}
                </p>
              </div>

              <div className="pt-8 border-t border-slate-50 flex items-center gap-12">
                {/* <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Sales Velocity
                  </p>
                  <p className="text-lg font-black text-slate-900">Moderate</p>
                </div> */}
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Stock Health
                  </p>
                  <p
                    className={`text-lg font-black ${quantity < 20 ? "text-rose-500" : "text-emerald-600"}`}
                  >
                    {quantity < 20 ? "Critically Low" : "Healthy"}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 flex gap-4">
              <AddStockModal
                name={activeProduct.name}
                id={activeProduct.id}
                stock={quantity}
                open={stockOpen}
                onOpenChange={setStockOpen}
              >
                <span className="w-full bg-emerald-50 text-emerald-700 py-5 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-emerald-100 border border-emerald-200 transition-all flex items-center justify-center gap-2">
                  <span>
                    <Plus className="size-4" />
                  </span>{" "}
                  Quick Add Stock
                </span>
              </AddStockModal>
              <DeleteProductDialog
                onClick={() =>
                  handleRemoveProduct(activeProduct.name, activeProduct.id)
                }
              >
                <span className="px-8 py-5 rounded-2xl text-rose-500 font-black text-xs uppercase tracking-widest hover:bg-rose-50 transition-all border border-red-500 hover:border-rose-100">
                  Remove Item
                </span>
              </DeleteProductDialog>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailsView;
